import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SwoodleLogo from "../../../Assets/Icons/SwoodleUpdatedLogo.svg";
import "./login.scss";
import TextInput from "../SignUp/TextInput";
import { NavLink, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { loginSchema } from "../../../utils/validationSchemas";
import { ReactComponent as CheckedBox } from "../../../Assets/Icons/checkedBox.svg";
import { ReactComponent as UncheckedBox } from "../../../Assets/Icons/uncheckedBlackBox.svg";
import { login } from "../../../services/authService";
import { setLoginData } from "../../../redux/slices/authSlice";
import { getUserSubscription } from "../../../services/subscriptionService";
import Loader from "../Loader";
import { getOnboardingStatus } from "../../../services/fundraiserService";
import { getFcmToken } from "../../../utils/connectStore";
import axios from "../../../utils/axios";
import PrimaryButton from "../PrimaryButton";
import { setCurrentBusiness } from "../../../redux/slices/merchantSlice";
import SwoodleFooter from "../SwoodleFooter";
import LoginHeader from "../LoginHeader";

const responsiveShellStyle = {
  width: "100%",
  maxWidth: "min(1920px, calc(100vw - 2vw))",
  margin: "0 auto",
};

const LoginSection = () => {
  const dispatch = useDispatch();
  const subLoading = useSelector((state) => state.subscriptionReducer.loading);
  const authLoading = useSelector((state) => state.authReducer.loading);
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [checked, setChecked] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [footerReady, setFooterReady] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setFooterReady(true), 150);
    return () => clearTimeout(timer);
  }, []);

  const onChecked = () => {
    setChecked(!checked);
  };

  const togglePassword = () => {
    setShowPassword(!showPassword);
  };

  const navigation = async (role, user) => {
    // NOTE: Approval status check is done in onSubmit BEFORE calling navigation()
    // If we reach here, the fundraiser is already approved (or not a fundraiser)
    switch (role) {
      case "merchant":
        navigate("/merchant/dashboard");
        break;
      case "fundraiser":
        // Check Stripe onboarding status (approval already verified in onSubmit)
        const statusPromise = await dispatch(getOnboardingStatus()).unwrap();
        if (
          statusPromise?.data?.detailsSubmitted === true &&
          statusPromise?.data?.chargesEnabled === true
        ) {
          navigate("/fundraiser/dashboard");
        } else if (
          statusPromise?.data?.detailsSubmitted === true &&
          statusPromise?.data?.chargesEnabled === false
        ) {
          navigate("/complete-setup");
        } else {
          navigate("/fundraiser/welcome");
        }
        break;
      case "admin":
        navigate("/dashboard");
        break;
      default:
        navigate("/dashboard");
    }
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: loginSchema,
    onSubmit: async (values, { setFieldError }) => {
      setIsCheckingStatus(true); // Show loader while checking
      try {
        const credentials = {
          email: values.email,
          password: values.password,
          fcmToken: getFcmToken(),
        };

        const loginWithRedux = async () => {
          await dispatch(
            login({
              email: credentials.email,
              password: credentials.password,
              fcmToken: credentials.fcmToken,
            })
          ).unwrap();

          dispatch(
            setLoginData({
              email: credentials.email,
              password: credentials.password,
              fcmToken: credentials.fcmToken,
            })
          );
        };

        // Step 1: Call login API DIRECTLY (without Redux) to get token without setting it in state
        // This prevents CommonRoutes from redirecting before we check status
        let loginResponse;
        try {
          loginResponse = await axios.post("auth/login", {
            email: credentials.email,
            password: credentials.password,
            fcmToken: credentials.fcmToken,
          });
        } catch (loginError) {
          setIsCheckingStatus(false);
          const errorMsg = loginError?.response?.data?.message || loginError?.message || "Invalid credentials";
          setFieldError("email", errorMsg);
          setFieldError("password", errorMsg);
          return;
        }

        const promiseResult = loginResponse?.data;
        if (!promiseResult || promiseResult?.success !== true) {
          setIsCheckingStatus(false);
          setFieldError("email", promiseResult?.message || "Invalid credentials");
          return;
        }

        const userRole = promiseResult?.data?.user?.role;
        const normalizedRole = typeof userRole === "string" ? userRole.toLowerCase() : userRole;

        // Check: If user role is "user" or "member", block login
        // This is the correct way to check - after successful login, check the actual role
        if (normalizedRole === "user" || normalizedRole === "member") {
          setIsCheckingStatus(false);
          setFieldError("password", "Invalid email. This email is registered as a member.");
          return;
        }

        // Step 2: For fundraisers ONLY - check Stripe status BEFORE admin approval
        // This ensures new accounts finish Stripe Connect before any approval checks
        if (normalizedRole === "fundraiser") {
          const tempToken = promiseResult?.data?.token;

          const fetchStripeStatus = async () => {
            const stripeStatusResponse = await axios.get("payout/onboardingStatus", {
              headers: {
                Authorization: `Bearer ${tempToken}`,
                "api-key": process.env.REACT_APP_API_KEY,
              },
            });
            const stripeStatus = stripeStatusResponse?.data?.data;
            return {
              detailsSubmitted: stripeStatus?.detailsSubmitted === true,
              chargesEnabled: stripeStatus?.chargesEnabled === true,
            };
          };

          let stripeDetailsSubmitted = false;
          let stripeChargesEnabled = false;
          try {
            const stripeStatus = await fetchStripeStatus();
            stripeDetailsSubmitted = stripeStatus.detailsSubmitted;
            stripeChargesEnabled = stripeStatus.chargesEnabled;
          } catch (stripeError) {
            // Failed to determine Stripe status; default to connect flow
            await loginWithRedux();
            setIsCheckingStatus(false);
            navigate("/fundraiser/welcome", { replace: true });
            return;
          }

          const stripeComplete = stripeDetailsSubmitted && stripeChargesEnabled;
          if (!stripeComplete) {
            await loginWithRedux();
            setIsCheckingStatus(false);

            if (stripeDetailsSubmitted && !stripeChargesEnabled) {
              navigate("/complete-setup", { replace: true });
            } else {
              navigate("/fundraiser/welcome", { replace: true });
            }
            return;
          }

          // Stripe is complete, now check admin approval status
          try {
            const checkResponse = await axios.get("fundraiser/profile", {
              headers: {
                Authorization: `Bearer ${tempToken}`,
                "api-key": process.env.REACT_APP_API_KEY,
              },
            });
            const fundraiserProfile = checkResponse?.data?.data;
            const profileStatusRaw = fundraiserProfile?.status;
            const profileStatus = typeof profileStatusRaw === "string" ? profileStatusRaw.toLowerCase() : profileStatusRaw;

            if (profileStatus === "declined") {
              setIsCheckingStatus(false);
              setFieldError("password", "Your account has been declined. Please contact support.");
              formik.setTouched({ password: true }, false);
              return;
            }

            if (profileStatus !== "approved") {
              setIsCheckingStatus(false);
              setFieldError("password", "Your account is pending admin approval. Please wait for approval.");
              formik.setTouched({ password: true }, false);
              return;
            }
          } catch (profileError) {
            setIsCheckingStatus(false);
            setFieldError("password", "Failed to verify account status. Please try again.");
            formik.setTouched({ password: true }, false);
            return;
          }
        }

        // Step 3: For non-fundraisers or approved fundraisers (handled above) - set token in Redux
        await dispatch(login({
          email: credentials.email,
          password: credentials.password,
          fcmToken: credentials.fcmToken,
        })).unwrap();

        setIsCheckingStatus(false); // Hide loader

        if (promiseResult?.data?.user?.role === "merchant") {
          const merchantProfiles = promiseResult?.data?.user?.merchantProfiles;
          if (merchantProfiles.length > 0) {
            dispatch(setCurrentBusiness(merchantProfiles[0]));
          }
        }

        // Step 4: Navigate directly
        navigation(promiseResult?.data?.user?.role, promiseResult?.data?.user);
        dispatch(
          setLoginData({
            email: credentials.email,
            password: credentials.password,
            fcmToken: credentials.fcmToken,
          })
        );

      } catch (err) {
        setIsCheckingStatus(false); // Hide loader on error
        // Normalize error for UX: email not found vs wrong password
        const status = err?.response?.status;
        const message = err?.response?.data?.message || err?.message || "Invalid credentials";
        if (status === 404 || /email/i.test(message)) {
          setFieldError("email", "Email doesn’t exist");
        } else if (status === 401) {
          setFieldError("password", "Incorrect password");
        } else {
          setFieldError("email", message);
        }
      }
    },
  });

  const { email, password } = formik.values;

  const { handleChange, handleBlur, handleSubmit, errors, touched } = formik;

  return (
    <form onSubmit={handleSubmit}>
      {subLoading || authLoading || isCheckingStatus ? <Loader /> : null}
      <div className="min-h-screen bg-white flex flex-col">
        <div className="w-full px-6 py-4">
          <div style={responsiveShellStyle}>
            <LoginHeader />
          </div>
        </div>

        <div className="flex-1 w-full flex flex-col items-center px-4">
          <div className="w-full max-w-[640px] mt-16">
            <h2 className="text-center text-[26px] md:text-[30px] font-[700] font-poppins text-[#303030]">Login</h2>
            <p className="text-center text-[#8C8C8C] mt-1 text-[14px] md:text-[15px]">Please provide your registered email to continue.</p>

            <div className="mt-6 space-y-4 flex flex-col items-center">
              <div className="w-[360px] sm:w-[420px] md:w-[460px]">
                <TextInput
                  name={"email"}
                  value={email}
                  placeholder={"Enter your email"}
                  inputClass={"h-full bg-[#F2F4F5] border border-[#EFF2F5] rounded-[12px] pl-8 pr-4 text-[16px] font-poppins font-[500] placeholder:text-[#8C8C8C]"}
                  innerDivClass={"h-[56px] w-full items-center"}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  error={errors.email}
                  touched={touched.email}
                  autoComplete={"email"}
                />
              </div>

              <div className="w-[360px] sm:w-[420px] md:w-[460px]">
                <TextInput
                  type={showPassword ? "text" : "password"}
                  name={"password"}
                  value={password}
                  placeholder={"Enter your password"}
                  inputClass={"h-full bg-[#F2F4F5] border border-[#EFF2F5] rounded-[12px] pl-8 pr-4 text-[16px] font-poppins font-[500] placeholder:text-[#8C8C8C]"}
                  innerDivClass={"h-[56px] w-full items-center"}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  error={errors.password}
                  touched={touched.password}
                  showPassword={showPassword}
                  togglePassword={togglePassword}
                  autoComplete={"current-password"}
                />
              </div>

              <div className="w-[360px] sm:w-[420px] md:w-[460px] flex justify-between items-center text-[13px] md:text-[14px] text-[#303030] font-poppins">
                <button type="button" className="flex items-center gap-0" onClick={onChecked}>
                  {checked ? <CheckedBox className="inputCheckbox" /> : <UncheckedBox className="inputCheckbox" />}
                  <span>Remember me</span>
                </button>
                <NavLink to="/forget-password" className="text-[#00ADEF]">Forget Password</NavLink>
              </div>
            </div>

            <div className="mt-4 flex justify-center">
              <PrimaryButton
                type="submit"
                className="w-[360px] sm:w-[420px] md:w-[460px] h-[60px] rounded-[8px] font-[600]"
                label="Log In"
              />
            </div>

            <div className="mt-4 text-center text-[14px] md:text-[15px] text-[#303030] font-poppins">
              Don't have an account?
              <span className="ml-2 font-[500] text-[#303030]">Register as</span>
              <NavLink to="/merchant/getting-started" className="ml-2 text-[#00ADEF] font-[600]">Merchant</NavLink>
              <span className="mx-1 text-[#00ADEF]">/</span>
              <NavLink to="/fundraiser/getting-started" className="text-[#00ADEF] font-[600]">Fundraiser</NavLink>
            </div>
          </div>
        </div>

        <div className={`mt-24 mb-8 w-full px-6 login-footer-shell ${footerReady ? "login-footer-shell--visible" : ""}`}>
          <SwoodleFooter
            containerStyle={responsiveShellStyle}
            className="login-footer-widget"
            enableEntranceAnimation={true}
          />
        </div>
      </div>
    </form>
  );
};

export default LoginSection;
